import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { lazy, Suspense } from "react";
import { AnimatePresence } from "framer-motion";
import './i18n/config';
import { PageTransition } from "./components/PageTransition";
import { AuthProvider } from "./providers/AuthProvider";
import { ViewAsProvider } from "./providers/ViewAsProvider";
import { PostHogProvider } from "./providers/PostHogProvider";
import { DeviceProvider } from "./contexts/DeviceContext";
import { InstallPrompt } from "./components/InstallPrompt";
import { RouteGuard } from "./components/RouteGuard";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { GlobalErrorBoundary } from "./components/GlobalErrorBoundary";
import { CompanionStatus } from "./components/CompanionStatus";
import { CookieBanner } from "./components/CookieBanner";
import { OnboardingTour } from "./components/OnboardingTour";
import { TourErrorBoundary } from "./components/TourErrorBoundary";
import { SmartOnboarding } from "./components/SmartOnboarding";
import { OfflineIndicator } from "./components/OfflineIndicator";
import { VersionChecker } from "./components/VersionChecker";
import { UpdateBanner } from "./components/UpdateBanner";
import { AIAssistant } from "./components/AIAssistant";
import { ViewAsIndicator } from "./components/ViewAsIndicator";
import { Loader2 } from "lucide-react";
import Landing from "./pages/Landing";
import {
  DashboardSkeleton,
  MediaGridSkeleton,
  MediaDetailSkeleton,
  SettingsSkeleton,
  ListSkeleton,
  GenericSkeleton,
} from "./components/skeletons";

// Lazy import helper with retry and cache-busting reload to avoid transient module fetch failures
function lazyWithRetry(importer: () => Promise<any>, retries = 2, delay = 300) {
  return lazy(async () => {
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await importer();
      } catch (err: any) {
        const isFetchError = typeof err?.message === 'string' && /Failed to fetch dynamically imported module/i.test(err.message);
        if (attempt === retries) {
          const url = new URL(window.location.href);
          const alreadyReloaded = sessionStorage.getItem('module_reload_once') === '1' || url.searchParams.has('v');
          if (alreadyReloaded) {
            try {
              const { toast } = await import("@/hooks/use-toast");
              toast({ title: "Update failed", description: "Still couldn't load the app. Try Clear Cache & Reload from Settings.", duration: 4000 });
              // Track error in boot markers
              const w = window as any;
              if (w.__BOOT_ERRORS && Array.isArray(w.__BOOT_ERRORS)) {
                w.__BOOT_ERRORS.push({ type: 'error', message: err?.message || 'module fetch failed' });
              }
            } catch {}
            throw err;
          }

          // Show user-friendly toast before reload
          try {
            const { toast } = await import("@/hooks/use-toast");
            toast({ title: "Updating app...", description: "Reloading to ensure you have the latest version.", duration: 2000 });
          } catch {}
          
          // Clear caches and SW
          try {
            if ('caches' in window) {
              const keys = await caches.keys();
              await Promise.all(keys.map((k) => caches.delete(k)));
            }
            if ('serviceWorker' in navigator) {
              const regs = await navigator.serviceWorker.getRegistrations();
              await Promise.all(regs.map((r) => r.unregister()));
            }
          } catch {}
          
          // Mark reload and reload with cache-busting param
          sessionStorage.setItem('module_reload_once', '1');
          setTimeout(() => {
            url.searchParams.set('v', String(Date.now()));
            window.location.replace(url.toString());
          }, 2100);
          throw err;
        }
        if (isFetchError) {
          await new Promise((r) => setTimeout(r, delay * (attempt + 1)));
          continue;
        }
        throw err;
      }
    }
    return importer();
  });
}

// Landing is eagerly imported above to avoid boot-time lazy failures

const PublicDownloads = lazyWithRetry(() => import("./pages/PublicDownloads"));
const InstallerTest = lazyWithRetry(() => import("./pages/InstallerTest"));
const ApiValidator = lazyWithRetry(() => import("./pages/ApiValidator"));
const RealtimeMonitoring = lazyWithRetry(() => import("./pages/RealtimeMonitoring"));
const BackendStatus = lazyWithRetry(() => import("./pages/BackendStatus"));
const Index = lazyWithRetry(() => import("./pages/Index"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const Setup = lazyWithRetry(() => import("./pages/Setup"));
const Status = lazyWithRetry(() => import("./pages/Status"));
const AuditLogs = lazyWithRetry(() => import("./pages/AuditLogs"));
const Jobs = lazyWithRetry(() => import("./pages/Jobs"));
const Transcoding = lazyWithRetry(() => import("./pages/Transcoding"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const Media = lazyWithRetry(() => import("./pages/Media"));
const MediaDetail = lazyWithRetry(() => import("./pages/MediaDetail"));
const FileManager = lazyWithRetry(() => import("./pages/FileManager"));
const Subscriptions = lazyWithRetry(() => import("./pages/Subscriptions"));
const Pair = lazyWithRetry(() => import("./pages/Pair"));
const Collection = lazyWithRetry(() => import("./pages/Collection"));
const Search = lazyWithRetry(() => import("./pages/Search"));
const Downloads = lazyWithRetry(() => import("./pages/Downloads"));
const IPTV = lazyWithRetry(() => import("./pages/IPTV"));
const PluginMarketplace = lazyWithRetry(() => import("./pages/PluginMarketplace"));
const PluginUpload = lazyWithRetry(() => import("./pages/PluginUpload"));
const PluginModeration = lazyWithRetry(() => import("./pages/PluginModeration"));
const Community = lazyWithRetry(() => import("./pages/Community"));
const Integrations = lazyWithRetry(() => import("./pages/Integrations"));
const Testing = lazyWithRetry(() => import("./pages/Testing"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const AdminDashboard = lazyWithRetry(() => import("./pages/AdminDashboard"));
const Legal = lazyWithRetry(() => import("./pages/Legal"));
const FAQ = lazyWithRetry(() => import("./pages/FAQ"));
const Features = lazyWithRetry(() => import("./pages/Features"));
const Privacy = lazyWithRetry(() => import("./pages/Privacy"));
const WhiteLabel = lazyWithRetry(() => import("./pages/WhiteLabel"));
const Testimonials = lazyWithRetry(() => import("./pages/Testimonials"));
const DeveloperAnalytics = lazyWithRetry(() => import("./pages/DeveloperAnalytics"));
const TourAnalytics = lazyWithRetry(() => import("./pages/TourAnalytics"));
const UserRoleManagement = lazyWithRetry(() => import("./pages/UserRoleManagement"));
const CookiePolicy = lazyWithRetry(() => import("./pages/CookiePolicy"));
const CookieConsentDashboard = lazyWithRetry(() => import("./pages/CookieConsentDashboard"));
const Watchlist = lazyWithRetry(() => import("./pages/Watchlist"));
const AnalyticsDashboard = lazyWithRetry(() => import("./pages/AnalyticsDashboard"));
const DeviceDashboard = lazyWithRetry(() => import("./pages/DeviceDashboard"));
const MFASetup = lazyWithRetry(() => import("./pages/MFASetup"));
const RoleManagement = lazyWithRetry(() => import("./pages/RoleManagement"));
const TranscodingManager = lazyWithRetry(() => import("./pages/TranscodingManager"));
const MediaServerSetup = lazyWithRetry(() => import("./pages/MediaServerSetup"));
const VPNSetup = lazyWithRetry(() => import("./pages/VPNSetup"));
const VPNStatus = lazyWithRetry(() => import("./pages/VPNStatus"));
const DesktopSettings = lazyWithRetry(() => import("./pages/DesktopSettings"));
const RemoteAccess = lazyWithRetry(() => import("./pages/RemoteAccess"));
const OfflineMedia = lazyWithRetry(() => import("./pages/OfflineMedia"));
const SetupWizardPage = lazyWithRetry(() => import("./pages/SetupWizard"));
const EnhancedAdminDashboard = lazyWithRetry(() => import("./pages/EnhancedAdminDashboard"));
const DeveloperMode = lazyWithRetry(() => import("./pages/DeveloperMode"));
const LegalIPTV = lazyWithRetry(() => import("./pages/LegalIPTV"));
const SecurityDashboard = lazyWithRetry(() => import("./pages/SecurityDashboard"));
const ComplianceDashboard = lazyWithRetry(() => import("./pages/ComplianceDashboard"));
const BiometricSetup = lazyWithRetry(() => import("./pages/BiometricSetup"));
const FeatureVoting = lazyWithRetry(() => import("./pages/FeatureVoting"));
const PluginBrowser = lazyWithRetry(() => import("./pages/PluginBrowser"));
const BackupDashboard = lazyWithRetry(() => import("./pages/BackupDashboard"));
const ScalingDashboard = lazyWithRetry(() => import("./pages/ScalingDashboard"));

// Smart loading fallback component with route-specific skeletons
const PageLoader = () => {
  const location = useLocation();
  const pathname = location.pathname;

  // Map routes to specific skeleton components
  if (pathname === '/dashboard' || pathname === '/home') {
    return <DashboardSkeleton />;
  }
  
  if (pathname === '/media') {
    return <MediaGridSkeleton />;
  }
  
  if (pathname.startsWith('/media/')) {
    return <MediaDetailSkeleton />;
  }
  
  if (pathname === '/settings' || pathname === '/transcoding') {
    return <SettingsSkeleton />;
  }
  
  if (
    pathname === '/downloads' ||
    pathname === '/search' ||
    pathname === '/collections' ||
    pathname.startsWith('/collection/') ||
    pathname === '/iptv' ||
    pathname === '/integrations' ||
    pathname === '/plugins' ||
    pathname === '/community'
  ) {
    return <ListSkeleton />;
  }

  // Generic skeleton for other routes
  return <GenericSkeleton />;
};

const AnimatedRoutes = () => {
  const location = useLocation();
  
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageTransition><Landing /></PageTransition>} />
        <Route path="/features" element={<PageTransition><Features /></PageTransition>} />
        <Route path="/privacy" element={<PageTransition><Privacy /></PageTransition>} />
        <Route path="/public-downloads" element={<PageTransition><PublicDownloads /></PageTransition>} />
        <Route path="/installer-test" element={<PageTransition><InstallerTest /></PageTransition>} />
        <Route path="/api-validator" element={<PageTransition><ApiValidator /></PageTransition>} />
        <Route path="/admin/monitoring" element={<PageTransition><RouteGuard requiredRoles={["admin"]}><RealtimeMonitoring /></RouteGuard></PageTransition>} />
        <Route path="/admin/backend-status" element={<PageTransition><RouteGuard requiredRoles={["admin"]}><BackendStatus /></RouteGuard></PageTransition>} />
        <Route path="/home" element={<PageTransition><RouteGuard><Index /></RouteGuard></PageTransition>} />
        <Route path="/auth" element={<PageTransition><Auth /></PageTransition>} />
        <Route path="/pair" element={<PageTransition><Pair /></PageTransition>} />
        <Route path="/dashboard" element={<PageTransition><RouteGuard><Dashboard /></RouteGuard></PageTransition>} />
        <Route path="/setup" element={<PageTransition><RouteGuard><Setup /></RouteGuard></PageTransition>} />
        <Route path="/status" element={<PageTransition><Status /></PageTransition>} />
        <Route path="/settings" element={<PageTransition><RouteGuard><Settings /></RouteGuard></PageTransition>} />
        <Route path="/logs" element={<PageTransition><RouteGuard requiredRoles={["admin"]}><AuditLogs /></RouteGuard></PageTransition>} />
        <Route path="/audit-logs" element={<PageTransition><RouteGuard requiredRoles={["admin"]}><AuditLogs /></RouteGuard></PageTransition>} />
        <Route path="/jobs" element={<PageTransition><RouteGuard><Jobs /></RouteGuard></PageTransition>} />
        <Route path="/transcoding" element={<PageTransition><RouteGuard><Transcoding /></RouteGuard></PageTransition>} />
        <Route path="/media" element={<PageTransition><RouteGuard><Media /></RouteGuard></PageTransition>} />
        <Route path="/media/:id" element={<PageTransition><RouteGuard><MediaDetail /></RouteGuard></PageTransition>} />
        <Route path="/collection/:id" element={<PageTransition><RouteGuard><Collection /></RouteGuard></PageTransition>} />
        <Route path="/search" element={<PageTransition><RouteGuard><Search /></RouteGuard></PageTransition>} />
        <Route path="/downloads" element={<PageTransition><RouteGuard><Downloads /></RouteGuard></PageTransition>} />
        <Route path="/files" element={<PageTransition><RouteGuard><FileManager /></RouteGuard></PageTransition>} />
        <Route path="/subscriptions" element={<PageTransition><RouteGuard><Subscriptions /></RouteGuard></PageTransition>} />
        <Route path="/iptv" element={<PageTransition><RouteGuard><IPTV /></RouteGuard></PageTransition>} />
        <Route path="/legal-iptv" element={<PageTransition><RouteGuard><LegalIPTV /></RouteGuard></PageTransition>} />
        <Route path="/plugins" element={<PageTransition><RouteGuard><PluginMarketplace /></RouteGuard></PageTransition>} />
        <Route path="/plugins/upload" element={<PageTransition><RouteGuard><PluginUpload /></RouteGuard></PageTransition>} />
        <Route path="/plugins/moderation" element={<PageTransition><RouteGuard requiredRoles={["admin"]}><PluginModeration /></RouteGuard></PageTransition>} />
        <Route path="/community" element={<PageTransition><RouteGuard><Community /></RouteGuard></PageTransition>} />
        <Route path="/integrations" element={<PageTransition><RouteGuard><Integrations /></RouteGuard></PageTransition>} />
        <Route path="/testing" element={<PageTransition><RouteGuard><Testing /></RouteGuard></PageTransition>} />
        <Route path="/admin" element={<PageTransition><RouteGuard requiredRoles={["admin"]}><AdminDashboard /></RouteGuard></PageTransition>} />
        <Route path="/legal" element={<PageTransition><Legal /></PageTransition>} />
        <Route path="/faq" element={<PageTransition><FAQ /></PageTransition>} />
        <Route path="/testimonials" element={<PageTransition><Testimonials /></PageTransition>} />
        <Route path="/analytics" element={<PageTransition><RouteGuard><DeveloperAnalytics /></RouteGuard></PageTransition>} />
        <Route path="/tour-analytics" element={<PageTransition><RouteGuard requiredRoles={["admin"]}><TourAnalytics /></RouteGuard></PageTransition>} />
        <Route path="/white-label" element={<PageTransition><RouteGuard><WhiteLabel /></RouteGuard></PageTransition>} />
        <Route path="/admin/roles" element={<PageTransition><RouteGuard requiredRoles={["admin"]}><UserRoleManagement /></RouteGuard></PageTransition>} />
        <Route path="/cookie-policy" element={<PageTransition><CookiePolicy /></PageTransition>} />
        <Route path="/admin/cookie-consent" element={<PageTransition><RouteGuard requiredRoles={["admin"]}><CookieConsentDashboard /></RouteGuard></PageTransition>} />
        <Route path="/watchlist" element={<PageTransition><RouteGuard><Watchlist /></RouteGuard></PageTransition>} />
        <Route path="/analytics-dashboard" element={<PageTransition><RouteGuard requiredRoles={["admin"]}><AnalyticsDashboard /></RouteGuard></PageTransition>} />
        <Route path="/devices" element={<PageTransition><RouteGuard><DeviceDashboard /></RouteGuard></PageTransition>} />
        <Route path="/mfa-setup" element={<PageTransition><RouteGuard><MFASetup /></RouteGuard></PageTransition>} />
        <Route path="/roles" element={<PageTransition><RouteGuard requiredRoles={["admin"]}><RoleManagement /></RouteGuard></PageTransition>} />
        <Route path="/transcoding-manager" element={<PageTransition><RouteGuard><TranscodingManager /></RouteGuard></PageTransition>} />
        <Route path="/media-servers" element={<PageTransition><RouteGuard><MediaServerSetup /></RouteGuard></PageTransition>} />
        <Route path="/vpn-setup" element={<PageTransition><RouteGuard><VPNSetup /></RouteGuard></PageTransition>} />
        <Route path="/vpn-status" element={<PageTransition><RouteGuard><VPNStatus /></RouteGuard></PageTransition>} />
        <Route path="/desktop-settings" element={<PageTransition><RouteGuard><DesktopSettings /></RouteGuard></PageTransition>} />
        <Route path="/remote-access" element={<PageTransition><RouteGuard><RemoteAccess /></RouteGuard></PageTransition>} />
        <Route path="/offline-media" element={<PageTransition><RouteGuard><OfflineMedia /></RouteGuard></PageTransition>} />
        <Route path="/setup-wizard" element={<PageTransition><RouteGuard><SetupWizardPage /></RouteGuard></PageTransition>} />
        <Route path="/admin-dashboard-enhanced" element={<PageTransition><RouteGuard requiredRoles={["admin"]}><EnhancedAdminDashboard /></RouteGuard></PageTransition>} />
        <Route path="/developer-mode" element={<PageTransition><RouteGuard><DeveloperMode /></RouteGuard></PageTransition>} />
        <Route path="/legal-iptv" element={<PageTransition><RouteGuard><LegalIPTV /></RouteGuard></PageTransition>} />
        <Route path="/security-dashboard" element={<PageTransition><RouteGuard requiredRoles={["admin"]}><SecurityDashboard /></RouteGuard></PageTransition>} />
        <Route path="/compliance-dashboard" element={<PageTransition><RouteGuard><ComplianceDashboard /></RouteGuard></PageTransition>} />
        <Route path="/biometric-setup" element={<PageTransition><RouteGuard><BiometricSetup /></RouteGuard></PageTransition>} />
        <Route path="/feature-voting" element={<PageTransition><RouteGuard><FeatureVoting /></RouteGuard></PageTransition>} />
        <Route path="/plugin-browser" element={<PageTransition><RouteGuard><PluginBrowser /></RouteGuard></PageTransition>} />
        <Route path="/admin/backup-dashboard" element={<PageTransition><RouteGuard requiredRoles={["admin"]}><BackupDashboard /></RouteGuard></PageTransition>} />
        <Route path="/admin/scaling" element={<PageTransition><RouteGuard requiredRoles={["admin"]}><ScalingDashboard /></RouteGuard></PageTransition>} />
        <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
      </Routes>
    </AnimatePresence>
  );
};

const App = () => (
  <HelmetProvider>
    <DeviceProvider>
      <BrowserRouter>
        <PostHogProvider>
          <AuthProvider>
            <ViewAsProvider>
              <GlobalErrorBoundary>
              <ErrorBoundary>
                <Toaster />
                <Sonner />
                <Suspense fallback={<PageLoader />}>
                  <AnimatedRoutes />
                </Suspense>
                <CompanionStatus />
                <InstallPrompt />
                <CookieBanner />
          <TourErrorBoundary 
            onAutoRecover={() => {
              // Dispatch event to trigger auto-recovery in OnboardingTour
              window.dispatchEvent(new CustomEvent("tour-auto-recover"));
            }}
          >
            <OnboardingTour />
          </TourErrorBoundary>
                <SmartOnboarding />
                <OfflineIndicator />
                <VersionChecker />
                <UpdateBanner />
                <ViewAsIndicator />
                <AIAssistant />
              </ErrorBoundary>
            </GlobalErrorBoundary>
            </ViewAsProvider>
          </AuthProvider>
        </PostHogProvider>
      </BrowserRouter>
    </DeviceProvider>
  </HelmetProvider>
);

export default App;
